// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { nativeVerticalScrollStyles } from 'components/common/scroll/nativeScrollStyles';
import { TabBar } from '@tls/treact-ui';
import PageContent from 'components/treactui-template/organisms/page/PageContent';
import { useNoiseConfig } from 'hooks/useNoiseConfig';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

type Tab = 'categories' | 'assets';

// the package gives its top-level sections no distinguishing class, tag them by content
function tagSections(host: HTMLElement) {
  host.querySelectorAll<HTMLElement>(':scope > section').forEach(section => {
    let kind = 'categories';
    if (section.querySelector('h2')) kind = 'headline';
    else if (section.querySelector('ux-input-field')) kind = 'assets';
    section.dataset.section = kind;
  });
}

export default function NotificationSettings() {
  const { t } = useTranslation();
  const { stage, locale, isUnavailable } = useNoiseConfig();
  const [tab, setTab] = useState<Tab>('categories');
  const hostRef = useRef<HTMLElement | null>(null);

  // sections appear when the lazily loaded component first renders
  const observe = useCallback((host: HTMLElement | null) => {
    hostRef.current = host;
    if (host) tagSections(host);
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const observer = new MutationObserver(() => tagSections(host));
    observer.observe(host, { childList: true });
    return () => observer.disconnect();
  }, [stage]);

  return (
    <PageContent displayAsPaper disableScroll>
      <Layout>
        {/* treact-ui TabBar is a vertical strip by design */}
        <TabStrip>
          <TabBar>
            <TabBar.ContentTabInformation
              label={t('settings.notifications.categories')}
              selected={tab === 'categories'}
              onClick={() => setTab('categories')}
              data-testid='settings-notifications-categories-tab'
            />
            <TabBar.ContentTabInformation
              label={t('settings.notifications.assets')}
              selected={tab === 'assets'}
              onClick={() => setTab('assets')}
              data-testid='settings-notifications-assets-tab'
            />
          </TabBar>
        </TabStrip>
        <Content $tab={tab} data-testid='settings-notifications'>
          {stage && <uxn-notification-settings ref={observe} stage={stage} locale={locale} />}
          {isUnavailable && <Unavailable>{t('settings.notifications.unavailable')}</Unavailable>}
        </Content>
      </Layout>
    </PageContent>
  );
}

const Layout = styled.div`
  display: flex;
  height: 100%;
  overflow: hidden;
`;

const TabStrip = styled.div`
  flex: 0 0 10rem;
`;

const Unavailable = styled.p`
  font-size: var(--uic-font-size-normal);
`;

const Content = styled.div<{ $tab: Tab }>`
  flex: 1 1 auto;
  ${nativeVerticalScrollStyles};
  padding: 1rem;
  display: flex;
  flex-direction: column;

  /* flex chain down to the asset table, whose max-height would otherwise leave the page half empty */
  uxn-notification-settings,
  uxn-notification-settings > section {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
  }

  uxn-notification-settings ux-scrollable-container.asset-table-container {
    flex: 1 1 auto;
    min-height: 12rem;
    max-height: none;
  }

  /* app resets box-sizing to content-box, ux-table row height sync then grows by padding each pass */
  uxn-notification-settings *,
  uxn-notification-settings *::before,
  uxn-notification-settings *::after {
    box-sizing: border-box;
  }

  /* headline and section titles are covered by page title and tabs */
  uxn-notification-settings > section[data-section='headline'],
  uxn-notification-settings > section > h3 {
    display: none;
  }

  uxn-notification-settings
    > section[data-section='${({ $tab }) => ($tab === 'categories' ? 'assets' : 'categories')}'] {
    display: none;
  }
`;
